import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addBasemap } from '@shared/lib/basemap';

/**
 * Read-only Leaflet map for a single panic incident. Drops a pulsing red
 * pin at the incident's coordinates and, if `accuracyM` is supplied, draws
 * a translucent accuracy circle around it.
 *
 * Uses vanilla Leaflet (not `react-leaflet`) so the dependency surface
 * matches the sibling resident-app and the runtime stays small.
 */
export interface PanicMapProps {
  lat: number;
  lng: number;
  accuracyM?: number | null;
  /** Initial zoom level. 17 is street-level. */
  zoom?: number;
}

/** Red pulsing marker — SVG inside a divIcon so no image assets are needed. */
function panicIcon(): L.DivIcon {
  return L.divIcon({
    className: '',
    html:
      '<div style="position:relative;width:36px;height:36px;">' +
      '  <span style="position:absolute;inset:0;border-radius:9999px;background:rgba(220,38,38,0.35);' +
      '    animation:panic-marker-ping 1.6s cubic-bezier(0,0,0.2,1) infinite;"></span>' +
      '  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" ' +
      '    fill="#dc2626" stroke="#ffffff" stroke-width="1.5" ' +
      '    style="position:relative;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35))">' +
      '    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>' +
      '    <circle cx="12" cy="10" r="2.6" fill="#ffffff" stroke="none"/>' +
      '  </svg>' +
      '</div>',
    iconSize: [36, 36],
    iconAnchor: [18, 34],
  });
}

export function PanicMap({ lat, lng, accuracyM, zoom = 17 }: PanicMapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);

  // Inject the keyframes for the marker ping once.
  useEffect(() => {
    const id = 'panic-marker-keyframes';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes panic-marker-ping {
        0%   { transform: scale(0.7); opacity: 0.75; }
        80%  { transform: scale(2.0); opacity: 0;    }
        100% { transform: scale(2.0); opacity: 0;    }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Initialise the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
    }).setView([lat, lng], zoom);

    addBasemap(map);

    const marker = L.marker([lat, lng], { icon: panicIcon() }).addTo(map);
    markerRef.current = marker;

    if (accuracyM && accuracyM > 0) {
      circleRef.current = L.circle([lat, lng], {
        radius: accuracyM,
        color: '#dc2626',
        weight: 1,
        fillColor: '#dc2626',
        fillOpacity: 0.12,
      }).addTo(map);
    }

    mapRef.current = map;
    // Container may still be settling — recalc after a tick.
    setTimeout(() => map.invalidateSize(), 120);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect coordinate / accuracy changes (rare — usually static per incident).
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;
    marker.setLatLng([lat, lng]);
    map.setView([lat, lng], map.getZoom());
    if (circleRef.current) {
      circleRef.current.setLatLng([lat, lng]);
      if (accuracyM && accuracyM > 0) circleRef.current.setRadius(accuracyM);
    } else if (accuracyM && accuracyM > 0) {
      circleRef.current = L.circle([lat, lng], {
        radius: accuracyM,
        color: '#dc2626',
        weight: 1,
        fillColor: '#dc2626',
        fillOpacity: 0.12,
      }).addTo(map);
    }
  }, [lat, lng, accuracyM]);

  return (
    <div
      ref={containerRef}
      className="h-72 w-full overflow-hidden rounded-xl border border-neutral-200"
      aria-label="Incident location map"
    />
  );
}
